import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WIDE_DESKTOP_MIN_WIDTH } from 'constants/breakpoints'
import { ECharts, EChartsOption } from 'echarts'
import ReactEChartsCore from 'echarts-for-react/lib/core'
import type { CallbackDataParams } from 'echarts/types/dist/shared.js'

import { IChartSeries } from '$/types'
import { useAppSelector } from 'app/hooks'
import Echart from 'common/components/Echart'
import { getBarColorScheme } from 'common/utils/chart/bar'
import {
  formatExponentialValue,
  formatLabelOnNewLine,
  formatWithCommasOrExponential,
  getChartLabelBasedOnRiskUnit,
  makeRUoptions
} from 'common/utils/chart/chartUtils'
import { getGroupColorScheme, QRABarColorScheme } from 'common/utils/colorUtils'

import GraphContainerCard from 'features/RiskManagement/Components/Graphs/Components/GraphContainerCard'
import GraphTypeToggle from 'features/RiskManagement/Components/Graphs/Components/GraphTypeToggle'
import { selectRiskUnit } from 'features/RiskManagement/Components/LocalFilter/localFilterSelectors'

import tailwind from '_/tailwind.config'
import { RiskForecastGraphs } from './RiskForecastGraphs'

export interface IRiskForecastChartYearSeriesItem {
  riskType: string
  value: number
}

interface IRiskForecastChart {
  years: number[]
  baseSeriesPerYear: IRiskForecastChartYearSeriesItem[][]
  compareSeriesPerYear?: IRiskForecastChartYearSeriesItem[][]
  showLoading: boolean
  s1Label?: string
  s2Label?: string
  colorScheme?: QRABarColorScheme
  isWideDesktop: boolean
}

interface ILegendSelected {
  name: string
  type: string
  selected: Record<string, boolean>
}

// #region helper method
interface IRiskValuesForTypeOverYears {
  [riskTypes: string]: number[]
}

// the handler is the third argument in the tuple
type EchartsOnHandler = Parameters<ECharts['on']>[2]

const getRiskValuesPerTypeOverYears = (
  seriesPerYear: IRiskForecastChartYearSeriesItem[][]
): IRiskValuesForTypeOverYears => {
  const riskValuesForType: IRiskValuesForTypeOverYears = {}

  seriesPerYear.forEach((seriesForYear, yearIdx) => {
    seriesForYear.forEach(({ riskType, value }) => {
      if (!Object.keys(riskValuesForType).includes(riskType)) {
        riskValuesForType[riskType] = []
      }

      // sparse insertion
      riskValuesForType[riskType][yearIdx] = value
    })
  })

  return riskValuesForType
}
// #endregion helper method

const RiskForecastChart = ({
  years,
  baseSeriesPerYear,
  compareSeriesPerYear,
  showLoading,
  s1Label,
  s2Label,
  colorScheme,
  isWideDesktop
}: IRiskForecastChart) => {
  const echartsRef = useRef<ReactEChartsCore>(null)
  const riskUnit = useAppSelector(selectRiskUnit)

  const [graphType, setGraphType] = useState<RiskForecastGraphs>(
    RiskForecastGraphs.ByThreat
  )

  const barSeries: IChartSeries[] = []
  const s1Values: number[] = []
  const s2Values: number[] = []

  baseSeriesPerYear.forEach((i) => {
    let tempvalue = 0

    i.forEach((j) => {
      tempvalue = tempvalue + j.value
    })
    s1Values.push(tempvalue)
  })

  if (compareSeriesPerYear != null) {
    compareSeriesPerYear.forEach((i) => {
      let tempvalue = 0

      i.forEach((j) => {
        tempvalue = tempvalue + j.value
      })
      s2Values.push(tempvalue)
    })
  }

  const deriveLegendFontSize = () => {
    const { innerWidth, devicePixelRatio } = window

    if (innerWidth < WIDE_DESKTOP_MIN_WIDTH) {
      return devicePixelRatio > 1 ? 10 : 11
    }

    return 12
  }

  const formatterOptions = makeRUoptions('ru')

  const { legendHeight, gridTop } = useMemo(() => {
    if (isWideDesktop) {
      return {
        legendHeight: 110,
        gridTop: 140
      }
    } else {
      return {
        legendHeight: 160,
        gridTop: 190
      }
    }
  }, [isWideDesktop])

  const stackedOption: EChartsOption = {
    legend: {
      orient: 'vertical',
      top: 0,
      height: legendHeight,
      textStyle: {
        fontSize: deriveLegendFontSize()
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      padding: 12,
      valueFormatter: (val) => formatExponentialValue(val as string, 2),
      formatter: (untypedParams): string | HTMLElement[] => {
        // Delayed typing to bypass non fully typed interface mismatch
        const params = untypedParams as CallbackDataParams[]

        const occurencesOfParams = params.reduce(
          (
            accumulator: {
              [key: string]: {
                value: string[]
                marker: CallbackDataParams['marker']
              }
            },
            param: CallbackDataParams
          ) => {
            const seriesName = param.seriesName || ''

            return {
              ...accumulator,
              [seriesName]: {
                ...(accumulator && accumulator[seriesName]),
                value: [
                  ...((accumulator && accumulator[seriesName]?.value) || []),
                  formatExponentialValue(param.value as string, 2)
                ],
                marker: param.marker
              }
            }
          },
          {}
        )

        let hasWIS = false
        const firstKey = Object.keys(occurencesOfParams)[0]

        if (occurencesOfParams[firstKey].value.length > 1) {
          hasWIS = true
        }

        const tooltipDiv = document.createElement('div')

        tooltipDiv.style.display = 'grid'
        tooltipDiv.style.columnGap = '8px'

        tooltipDiv.style.gridTemplateColumns = hasWIS
          ? 'auto auto auto'
          : 'auto auto'

        const nameLabel = document.createElement('div')

        nameLabel.innerText = params[0].name
        nameLabel.style.fontWeight = '600'

        const titleLabel1 = document.createElement('div')

        titleLabel1.innerText =
          graphType === RiskForecastGraphs.ByThreat ? `${s1Label}` : ''
        titleLabel1.style.fontWeight = '700'
        tooltipDiv.appendChild(nameLabel)
        tooltipDiv.appendChild(titleLabel1)

        if (hasWIS) {
          const titleLabel2 = document.createElement('div')

          titleLabel2.style.fontWeight = '700'

          titleLabel2.innerText =
            graphType === RiskForecastGraphs.ByThreat ? `${s2Label}` : ''
          tooltipDiv.appendChild(titleLabel2)
        }

        Object.keys(occurencesOfParams).forEach((seriesName) => {
          const labelDiv = document.createElement('div')

          labelDiv.style.display = 'flex'
          labelDiv.style.gap = '5px'

          const label = document.createElement('span')
          const marker = document.createElement('span')

          marker.innerHTML = occurencesOfParams[seriesName].marker as string
          label.innerText = seriesName
          labelDiv.appendChild(marker)
          labelDiv.appendChild(label)
          tooltipDiv.appendChild(labelDiv)

          occurencesOfParams[seriesName].value.forEach((val: string) => {
            const valueLabel = document.createElement('div')

            valueLabel.innerText = val
            valueLabel.style.fontWeight = '500'
            valueLabel.style.width = '60px'
            tooltipDiv.appendChild(valueLabel)
          })
        })

        return [tooltipDiv]
      }
    },
    grid: {
      top: gridTop,
      left: 76,
      right: 24,
      bottom: 42
    },
    xAxis: {
      data: years,
      name: 'Year',
      nameLocation: 'middle',
      nameGap: 32,
      nameTextStyle: {
        color: tailwind.theme.colors.black,
        fontWeight: 'bold',
        align: 'center'
      },
      axisLabel: {
        formatter: (val: number) => formatLabelOnNewLine(val),
        lineHeight: 16,
        interval: 0
      },
      axisLine: {
        // un-bold the xAxis
        show: false
      },
      axisTick: {
        show: false
      }
    },
    yAxis: {
      name: getChartLabelBasedOnRiskUnit(riskUnit),
      nameLocation: 'middle',
      nameRotate: 90,
      // REVIEW: echarts#9265
      nameGap: 62,
      nameTextStyle: {
        color: tailwind.theme.colors.black,
        fontWeight: 'bold'
      },
      axisLabel: {
        formatter: (value: string) =>
          formatWithCommasOrExponential(value, formatterOptions)
      }
    },
    color: getBarColorScheme(getGroupColorScheme(baseSeriesPerYear[0]?.length))
  }

  const baselineSeries = Object.entries(
    getRiskValuesPerTypeOverYears(baseSeriesPerYear)
  ).map(([riskType, values]) => {
    return {
      type: 'bar',
      stack: 'baseline',
      name: riskType,
      data: values
    }
  })

  const compareSeries =
    compareSeriesPerYear != null
      ? Object.entries(getRiskValuesPerTypeOverYears(compareSeriesPerYear)).map(
          ([riskType, values]) => {
            return {
              type: 'bar',
              stack: 'compare',
              name: riskType,
              data: values
            }
          }
        )
      : []

  stackedOption.series = [...baselineSeries, ...compareSeries] as any

  if (s1Values != null) {
    barSeries.push({
      label: s1Label,
      values: s1Values
    })
  }

  if (s2Values[0] !== 0) {
    barSeries.push({
      label: s2Label,
      values: s2Values
    })
  }

  const barOption: EChartsOption = {
    tooltip: stackedOption.tooltip,
    grid: {
      top: 30,
      left: 50,
      right: 24,
      bottom: 24,
      containLabel: true
    },
    legend: {
      show: barSeries.length > 1
    },
    xAxis: stackedOption.xAxis,
    yAxis: {
      name: getChartLabelBasedOnRiskUnit(riskUnit),
      nameLocation: 'middle',
      // REVIEW: echarts#9265
      nameGap: 80,
      nameTextStyle: {
        color: tailwind.theme.colors.black,
        fontWeight: 'bold'
      },
      axisLabel: {
        formatter: (value: string) =>
          formatWithCommasOrExponential(value, formatterOptions)
      }
    },
    series: barSeries.map((s, index) => {
      return {
        type: 'bar',
        name: s.label,
        data: s.values,
        color: getBarColorScheme(colorScheme)[index]
      }
    })
  }

  const setLegendToValue = (legend: string, boolValue: boolean) => {
    echartsRef.current?.getEchartsInstance().setOption({
      legend: {
        selected: {
          [legend]: boolValue
        }
      }
    })
  }

  const numberOfSelectedLegends = (
    selectedLegends: Record<string, boolean>
  ): number => {
    return Object.keys(selectedLegends).reduce((amount, legend) => {
      if (selectedLegends[legend]) {
        amount += 1
      }

      return amount
    }, 0)
  }

  const handleLegendClicked = useCallback<EchartsOnHandler>((arg) => {
    const legendsStatus = arg as ILegendSelected

    const totalLegends = Object.keys(legendsStatus.selected).length
    const totalSelectedLegends = numberOfSelectedLegends(legendsStatus.selected)

    const werePreviouslyAllSelected =
      !legendsStatus.selected[legendsStatus.name]

    if (
      totalSelectedLegends === totalLegends - 1 &&
      werePreviouslyAllSelected
    ) {
      Object.keys(legendsStatus.selected).forEach((legend) => {
        if (legend !== legendsStatus.name) {
          setLegendToValue(legend, false)
        } else {
          setLegendToValue(legend, true)
        }
      })
    } else if (totalSelectedLegends === 0) {
      Object.keys(legendsStatus.selected).forEach((legend) => {
        setLegendToValue(legend, true)
      })
    }
  }, [])

  useEffect(() => {
    const echartsInstance = echartsRef.current?.getEchartsInstance()

    if (echartsInstance == null || echartsInstance.isDisposed()) {
      return
    }

    echartsInstance.on('legendselectchanged', handleLegendClicked)

    return () => {
      if (echartsInstance.isDisposed()) {
        echartsInstance.off('legendselectchanged', handleLegendClicked)
      }
    }
  }, [handleLegendClicked])

  return (
    <GraphContainerCard
      title='Risk Forecast'
      optionsComponent={
        <GraphTypeToggle graphType={graphType} setGraphType={setGraphType} />
      }
    >
      <Echart
        notMerge
        ref={echartsRef}
        option={
          graphType === RiskForecastGraphs.ByThreat ? stackedOption : barOption
        }
        showLoading={showLoading}
      />
    </GraphContainerCard>
  )
}

export default RiskForecastChart
